import { useQuery } from '@tanstack/react-query';
import instance from '@src/api/axiosInstance';
import { logger } from '@src/utils/misc/logger';
import { trackQueryError } from '@src/utils/sensor/apptracker';
import { supportedEventTypes } from '../../utils';

interface ViewShareResponse {
  events: Array<any>;
  state: any;
  metadata: {
    app_name: string;
    created_by: string;
    shared_timestamp: string;
  };
}

const parseSharedEvents = (events: Array<any>) => {
  const parsedEvents: Array<{ [key: string]: any }> = [];

  events.forEach((item) => {
    const { id, timestamp, content, author, error } = item;

    // Handle error events
    if (error) {
      parsedEvents.push({
        id,
        author,
        type: 'error',
        value: {
          message: error.message || 'An error occurred. Please try again.',
          details: error.code || null,
        },
        timestamp,
      });
      return;
    }

    // Handle regular events with content
    if (content) {
      content.parts.forEach((part: any) => {
        for (const [key, value] of Object.entries(part)) {
          // Only process supported event types
          if (supportedEventTypes.includes(key)) {
            // Check if this part has a thought field
            const isThought = part.thought === true;

            parsedEvents.push({
              id,
              author,
              type: key,
              value,
              timestamp,
              ...(isThought && { thought: true }),
            });
          }
        }
      });
    }
  });

  return parsedEvents;
};

export const useViewShare = (shareId: string | undefined) => {
  const { data, isFetching, isError, error } = useQuery(
    ['nexa-shared-session', shareId],
    async () => {
      try {
        const response = await instance.get<ViewShareResponse>(`/nexa/share/${shareId}`);
        const events = parseSharedEvents(response.data.events);
        logger.log('Shared session loaded', { share_id: shareId, event_count: events.length });

        return {
          events,
          state: response.data.state,
          metadata: response.data.metadata,
        };
      } catch (err: any) {
        logger.error('Failed to load shared session', err);
        trackQueryError({ type: 'nexa-shared-session', err });
        throw err;
      }
    },
    {
      enabled: !!shareId,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 0,
      cacheTime: 0,
    },
  );

  return {
    events: data?.events || [],
    state: data?.state || {},
    metadata: data?.metadata,
    loading: isFetching,
    error: isError ? error : null,
  };
};
